import type { storyDefaultDecorator } from "@/story-utils/storybookDefaultDecorator.tsx";
import { isStorybook } from "@/utils/environment.ts";
import { lazy } from "@/utils/lazy.ts";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import type { SetStateAction, WritableAtom } from "jotai/vanilla";
import { atom } from "jotai/vanilla";
import type { SyncStorage } from "jotai/vanilla/utils/atomWithStorage";

export type AnyWritableAtom = WritableAtom<unknown, any[], any>;
export type AtomValues = readonly [AnyWritableAtom, unknown][];

// Lazily create the storages so this module can be imported in non-browser environments

export const jsonLocalStorage = /*#__PURE__*/ lazy(
    (): SyncStorage<unknown> => createJSONStorage(() => window.localStorage),
);
export const jsonSessionStorage = /*#__PURE__*/ lazy(
    (): SyncStorage<unknown> => createJSONStorage(() => window.sessionStorage),
);

/**
 * Works around [Can't access `atomWithStorage` value in SPA initial](https://github.com/pmndrs/jotai/issues/2240)
 * @__NO_SIDE_EFFECTS__
 */
export function atomWithLocalStorage<T>(key: string, initialValue: T) {
    return isStorybook
        ? atomWithJsonRoundTrip(initialValue)
        : atomWithStorage<T>(key, initialValue, jsonLocalStorage() as SyncStorage<T>, { getOnInit: true });
}

/** @see {@link atomWithLocalStorage}
 * @__NO_SIDE_EFFECTS__
 */
export function atomWithSessionStorage<T>(key: string, initialValue: T) {
    return isStorybook
        ? atomWithJsonRoundTrip(initialValue)
        : atomWithStorage<T>(key, initialValue, jsonSessionStorage() as SyncStorage<T>, { getOnInit: true });
}

/**
 * Creates a regular Jotai atom that simulates storage behavior by round-tripping values through JSON.
 *
 * Used in Storybook to avoid browser storage side effects. The value gets reset whenever the story is switched because
 * the {@link storyDefaultDecorator} re-creates the Jotai provider.
 *
 * @__NO_SIDE_EFFECTS__
 */
export function atomWithJsonRoundTrip<T>(initialValue: T) {
    const baseAtom = atom(initialValue);
    return atom(
        (get) => get(baseAtom),
        (get, set, newValue: SetStateAction<T>) => {
            const prev = get(baseAtom);
            const value = typeof newValue === "function" ? (newValue as (prev: T) => T)(prev) : newValue;
            const roundTripped = JSON.parse(JSON.stringify(value)) as T;
            set(baseAtom, roundTripped);
        },
    );
}
